/* eslint-disable camelcase */
/* global google */
import env from '@beam-australia/react-env'
import { useJsApiLoader } from '@react-google-maps/api'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { debounce } from 'lodash'

import { mapPointer } from '../../../../../assets'
import { Address } from '../../../../../ducks/types'
import { flattenedAddress } from '../../../../../helpers'
import { SelectAddressModalProps, PlaceOption } from './types'

const GOOGLE_MAPS_API_KEY = env('GOOGLE_MAPS_API_KEY') ?? ''
const START_SEARCHING_FROM = 3

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    position: 'relative',
    width: '100%',
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
    height: '54px',
    padding: '0 44px 0 16px',
    borderRadius: '8px',
    border: '2px solid var(--border-color)',
    color: 'var(--text-color)',
    fontFamily: 'LatoNormal',
    fontWeight: 400,
    fontSize: 16,
    backgroundColor: 'var(--white-color)',
    boxSizing: 'border-box',
  },
  inputFocus: {
    borderColor: 'var(--border-color-focus)',
    outline: 'none',
  },
  searchIcon: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 20,
    height: 20,
    pointerEvents: 'none',
    color: 'var(--placeholder-color)',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    maxHeight: 280,
    overflowY: 'auto',
    backgroundColor: 'var(--white-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 999999,
    listStyle: 'none',
    padding: 0,
    marginBottom: 0,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    cursor: 'pointer',
    borderBottom: '1px solid var(--border-color)',
    gap: 8,
    minHeight: 48,
  },
  optionLast: {
    borderBottom: 'none',
  },
  optionHover: {
    backgroundColor: 'var(--row-focus)',
  },
  optionIcon: {
    width: 24,
    height: 24,
    flexShrink: 0,
  },
  optionLabel: {
    fontSize: 16,
    fontFamily: 'Lato',
    color: 'var(--text-color)',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}

const SelectAddressModal: FC<SelectAddressModalProps> = ({
  onChange,
  placeholder = 'Address',
  value: controlledValue,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [addressText, setAddressText] = useState(controlledValue ?? '')
  const [options, setOptions] = useState<PlaceOption[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [inputFocused, setInputFocused] = useState(false)

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: `${GOOGLE_MAPS_API_KEY}&loading=async`,
  })

  const placesService = useRef<google.maps.places.PlacesService | null>(null)
  const autoCompleteService = useRef<google.maps.places.AutocompleteService | null>(null)

  useEffect(() => {
    if (typeof controlledValue === 'string') {
      setAddressText(controlledValue)
    }
  }, [controlledValue])

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places) return
    const div = document.createElement('div')
    placesService.current = new window.google.maps.places.PlacesService(div)
    autoCompleteService.current = new window.google.maps.places.AutocompleteService()
    return () => {
      placesService.current = null
      autoCompleteService.current = null
    }
  }, [isLoaded])

  const fetchPredictions = useCallback((value: string) => {
    if (!value || value.length < START_SEARCHING_FROM) {
      setOptions([])
      setIsOpen(false)
      return
    }
    const ac = autoCompleteService.current
    if (!ac) return
    setLoading(true)
    ac.getPlacePredictions(
      {
        input: value,
        componentRestrictions: { country: ['us', 'ca'] },
      },
      (predictions) => {
        setLoading(false)
        if (!predictions?.length) {
          setOptions([])
          setIsOpen(false)
          return
        }
        const opts: PlaceOption[] = predictions.map((p) => ({
          id: p.place_id ?? '',
          label: p.description ?? '',
          value: p.description ?? '',
        }))
        setOptions(opts)
        setIsOpen(true)
        setFocusedIndex(-1)
      }
    )
  }, [])

  const debouncedFetch = useCallback(debounce(fetchPredictions, 500), [fetchPredictions])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
    setAddressText(v)
    setOptions([])
    if (v.length >= START_SEARCHING_FROM) {
      debouncedFetch(v)
    } else {
      setIsOpen(false)
      debouncedFetch.cancel()
    }
  }

  const handleSelectOption = (option: PlaceOption) => {
    const ps = placesService.current
    if (!ps) return
    ps.getDetails(
      { placeId: option.id },
      (place) => {
        if (!place) return
        const {
          city,
          state,
          zipCode,
          line_1,
          street_number,
          latitude,
          longitude,
          country,
          county,
          fullAddress,
          googleUrl,
        } = flattenedAddress(place)

        setAddressText(option.label)
        setOptions([])
        setIsOpen(false)

        const address: Address = {
          city: city ?? '',
          state: state ?? '',
          zipCode: zipCode ?? '',
          line_1: `${street_number ?? ''} ${line_1 ?? ''}`.trim(),
          line_2: '',
          latitude,
          longitude,
          country: country ?? '',
          county: county ?? '',
          fullAddress,
          googleUrl,
        }
        onChange(address)
      }
    )
  }

  const handleBlur = () => {
    setInputFocused(false)
    setFocusedIndex(-1)
    setTimeout(() => {
      setIsOpen(false)
    }, 200)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || !options.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex((i) => (i < options.length - 1 ? i + 1 : i))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex((i) => (i > 0 ? i - 1 : -1))
    } else if (e.key === 'Enter' && focusedIndex >= 0 && options[focusedIndex]) {
      e.preventDefault()
      handleSelectOption(options[focusedIndex])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setFocusedIndex(-1)
    }
  }

  return (
    <div ref={wrapperRef} style={styles.wrapper}>
      <div style={styles.inputWrapper}>
        <input
          type="text"
          value={addressText}
          onChange={handleInputChange}
          onFocus={() => setInputFocused(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{
            ...styles.input,
            ...(inputFocused ? styles.inputFocus : {}),
          }}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />
        <svg
          style={styles.searchIcon}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
        </svg>
      </div>

      {isOpen && options.length > 0 && (
        <ul
          style={styles.dropdown}
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
        >
          {options.map((option, index) => (
            <li
              key={option.id}
              role="option"
              aria-selected={index === focusedIndex}
              style={{
                ...styles.option,
                ...(index === options.length - 1 ? styles.optionLast : {}),
                ...(index === focusedIndex ? styles.optionHover : {}),
              }}
              onMouseEnter={() => setFocusedIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault()
                handleSelectOption(option)
              }}
            >
              <img
                src={mapPointer}
                alt=""
                style={styles.optionIcon}
              />
              <span style={styles.optionLabel}>{option.label}</span>
            </li>
          ))}
        </ul>
      )}

      {loading && isOpen && (
        <ul style={styles.dropdown} role="listbox">
          <li style={{ ...styles.option, ...styles.optionLast, cursor: 'default' }}>
            <span style={styles.optionLabel}>Buscando...</span>
          </li>
        </ul>
      )}
    </div>
  )
}

export default SelectAddressModal
